import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FolderPlus, Save, Sparkles, Trash2, Wand2, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AILabShell from "@/components/ai-lab/AILabShell";
import FieldRenderer from "@/components/ai-lab/FieldRenderer";
import SaveBrandProfileModal from "@/components/ai-lab/SaveBrandProfileModal";
import { BRAND_PROFILE_SECTIONS, EMPTY_BRAND_PROFILE, brandProfileCompleteness } from "@/data/ai-lab/brandProfileSchema";
import { useBrandProfile } from "@/hooks/useBrandProfile";

/**
 * BrandProfilePage — route: /ai-cpg-tools/brand-profile
 *
 * Two modes:
 *   1. Normal editor — founder fills / updates their brand profile. All
 *      shell actions (Save profile, Save to Project, Delete) are visible.
 *   2. Preview mode — reached via `?snapshot=<projectId>`. Fields are
 *      pre-filled from the saved snapshot but the live profile in the
 *      backend is left untouched. The founder confirms with
 *      "Replace and Save" or cancels (page reload restores the live copy).
 */
export default function BrandProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const snapshotId = searchParams.get("snapshot") || null;

  const { profile, hasProfile, save, clear, saving, error, loading } = useBrandProfile();
  const [form, setForm] = useState({ ...EMPTY_BRAND_PROFILE });
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saveToProjectOpen, setSaveToProjectOpen] = useState(false);

  // Preview mode state — populated when opening a project snapshot.
  const [snapshotMeta, setSnapshotMeta] = useState(null); // { name, brand_profile_snapshot, ... }
  const [snapshotLoading, setSnapshotLoading] = useState(!!snapshotId);
  const previewMode = !!snapshotMeta;

  // Hydrate form from live profile whenever it changes — but only in
  // normal (non-preview) mode so a snapshot preview isn't overwritten.
  useEffect(() => {
    if (previewMode) return;
    if (profile) setForm({ ...EMPTY_BRAND_PROFILE, ...profile });
  }, [profile, previewMode]);

  // Load snapshot when `?snapshot=<id>` is present.
  useEffect(() => {
    if (!snapshotId) return;
    let cancelled = false;
    setSnapshotLoading(true);
    api
      .get(`/projects/${snapshotId}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (!data || data.type !== "brand_profile" || !data.brand_profile_snapshot) {
          toast.error("This project is not an AI Brand Profile snapshot.");
          navigate("/ai-cpg-tools/brand-profile", { replace: true });
          return;
        }
        setSnapshotMeta(data);
        setForm({ ...EMPTY_BRAND_PROFILE, ...data.brand_profile_snapshot });
        setDirty(false);
      })
      .catch(() => {
        toast.error("Could not load this saved profile.");
        navigate("/ai-cpg-tools/brand-profile", { replace: true });
      })
      .finally(() => !cancelled && setSnapshotLoading(false));
    return () => {
      cancelled = true;
    };
  }, [snapshotId, navigate]);

  const completeness = brandProfileCompleteness(form);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    for (const s of BRAND_PROFILE_SECTIONS)
      for (const f of s.fields)
        if (f.required && !String(form[f.key] || "").trim())
          errs[f.key] = `${f.label} is required`;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSave = async (thenGo) => {
    if (!validate()) {
      toast.error("Please fill in the required brand basics.");
      return;
    }
    await save(form);
    setDirty(false);
    toast.success("Brand profile saved. Every prompt will use it.");
    if (thenGo) navigate("/ai-cpg-tools/prompt-builder");
  };

  const onClear = async () => {
    if (!window.confirm("Delete your brand profile? Prompts will stop auto-filling.")) return;
    await clear();
    setForm({ ...EMPTY_BRAND_PROFILE });
    setDirty(false);
    toast.success("Brand profile removed.");
  };

  // ── Preview-mode actions ────────────────────────────────────────────────
  // Cancel: per spec, refresh so the live profile is restored on screen.
  const onCancelPreview = () => {
    navigate("/ai-cpg-tools/brand-profile", { replace: true });
    // Full reload so the useBrandProfile hook re-fetches the untouched
    // live copy and any local edits vanish — matches "restore current".
    window.location.reload();
  };

  // Replace and Save: overwrite the live profile with the snapshot fields.
  // Required-fields validation stays enforced because the backend
  // /ai-lab/brand-profile endpoint rejects any profile that is missing
  // brandName / category / productDescription. If the snapshot pre-dates
  // one of those requirements the user can fill it in-place before
  // clicking Replace and Save again — the form is fully editable in
  // preview mode for exactly this reason.
  const onReplaceAndSave = async () => {
    if (!validate()) {
      toast.error("This snapshot is missing a required field. Please fill it in above, then click Replace and Save again.");
      return;
    }
    await save(form);
    setDirty(false);
    setSnapshotMeta(null);
    toast.success("Live brand profile replaced with the saved snapshot.");
    navigate("/ai-cpg-tools/brand-profile", { replace: true });
  };

  // ── Header actions ──────────────────────────────────────────────────────
  const headerActions = previewMode ? (
    <>
      <Button
        variant="ghost"
        onClick={onCancelPreview}
        data-testid="brand-profile-preview-cancel"
      >
        <X className="mr-2 h-4 w-4" /> Cancel
      </Button>
      <Button
        onClick={onReplaceAndSave}
        disabled={saving || loading}
        className="bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="brand-profile-preview-replace-save"
      >
        <Check className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Replace and Save"}
      </Button>
    </>
  ) : (
    <>
      {hasProfile && (
        <Button
          variant="ghost"
          onClick={onClear}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </Button>
      )}
      <Button
        variant="outline"
        onClick={() => setSaveToProjectOpen(true)}
        disabled={saving || loading}
        data-testid="brand-profile-save-to-project-btn"
      >
        <FolderPlus className="mr-2 h-4 w-4" /> Save to Project
      </Button>
      <Button
        onClick={() => onSave(false)}
        disabled={saving || loading}
        data-testid="brand-profile-save-btn"
      >
        <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save profile"}
      </Button>
    </>
  );

  return (
    <AILabShell
      crumbs={[{ label: "Brand Profile" }]}
      title="AI Brand Profile"
      subtitle="Enter your brand information once. Every prompt in AI CPG Tools will include it automatically, so you never retype your positioning, consumer, claims or competitors."
      actions={headerActions}
    >
      {/* Preview-mode banner */}
      {previewMode && (
        <div
          className="mb-6 flex flex-col gap-2 rounded-xl border border-violet-300 bg-violet-50 p-4 dark:bg-violet-950/30 sm:flex-row sm:items-center sm:justify-between"
          data-testid="brand-profile-preview-banner"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white">
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-violet-900 dark:text-violet-100">
                Previewing saved snapshot: {snapshotMeta?.name || "AI Brand Profile"}
              </p>
              <p className="text-xs text-violet-800/80 dark:text-violet-200/70 mt-0.5">
                Your live brand profile is untouched. Click <strong>Replace and Save</strong> to
                overwrite it, or <strong>Cancel</strong> to keep your current profile.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-600 text-white">
            <Sparkles className="h-5 w-5" />
          </span>
          <div>
            <p className="font-semibold">{completeness}% complete</p>
            <p className="text-xs text-muted-foreground">
              {completeness < 40
                ? "Fill the basics and positioning first — they power 90% of prompts."
                : completeness < 80
                ? "Good. Personality, voice and visual identity make design prompts much sharper."
                : "Excellent — your prompts will be fully personalized."}
            </p>
          </div>
        </div>
        <Progress value={completeness} className="h-2 sm:w-56" />
      </div>

      {error && (
        <p className="mb-4 rounded-md border border-pink-300 bg-pink-50 p-3 text-sm dark:bg-pink-950/30">
          {error}
        </p>
      )}

      {snapshotLoading ? (
        <p className="text-sm text-muted-foreground">Loading saved profile…</p>
      ) : (
        <div className="space-y-6">
          {BRAND_PROFILE_SECTIONS.map((section) => (
            <Card key={section.id}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{section.title}</CardTitle>
                <p className="text-sm text-muted-foreground">{section.description}</p>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {section.fields.map((f) => (
                    <FieldRenderer
                      key={f.key}
                      field={{ ...f, id: f.key, span: f.type === "textarea" || f.type === "urls" ? "full" : undefined }}
                      value={form[f.key] ?? ""}
                      onChange={(v) => setField(f.key, v)}
                      error={errors[f.key]}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="sticky bottom-0 mt-6 -mx-4 border-t bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {previewMode
              ? "Snapshot preview — your live profile is safe until you click Replace and Save."
              : dirty
              ? "You have unsaved changes."
              : hasProfile
              ? "Saved. You can update this any time."
              : "Not saved yet."}
          </p>
          <div className="flex gap-2">
            {previewMode ? (
              <>
                <Button variant="outline" onClick={onCancelPreview} data-testid="brand-profile-preview-cancel-footer">
                  <X className="mr-2 h-4 w-4" /> Cancel
                </Button>
                <Button
                  onClick={onReplaceAndSave}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="brand-profile-preview-replace-save-footer"
                >
                  <Check className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Replace and Save"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => onSave(false)} disabled={saving}>
                  <Save className="mr-2 h-4 w-4" /> Save
                </Button>
                <Button
                  onClick={() => onSave(true)}
                  disabled={saving}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  <Wand2 className="mr-2 h-4 w-4" /> Save & open Prompt Builder
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {saveToProjectOpen && (
        <SaveBrandProfileModal
          profile={form}
          defaultName={form.brandName ? `${form.brandName} — AI Brand Profile` : undefined}
          onCancel={() => setSaveToProjectOpen(false)}
          onSaved={() => setSaveToProjectOpen(false)}
        />
      )}
    </AILabShell>
  );
}
